import type { Side } from './hands';

/**
 * The 16 suit combos of a hand form a 4×4 grid: rows are the first card's suit, columns the second's, the
 * diagonal the 4 suited combos, the rest the 12 offsuit ones. Cards in our hand block whole lines of it:
 * a card of the first rank blocks its suit's row, a card of the second rank its suit's column.
 */
/** how many lines: a fixed 0–3, or 'random' = drawn per term */
export type LineCount = number | 'random';
export interface Blockers {
  /** blocked rows (horizontal lines) */
  rows: LineCount;
  /** blocked columns (vertical lines) */
  cols: LineCount;
}
export const LINE_COUNTS: readonly LineCount[] = [0, 1, 2, 3, 'random'];
/** the counts a setting can resolve to */
export const countsOf = (n: LineCount): readonly number[] => (n === 'random' ? [0, 1, 2, 3] : [n]);
export const NO_BLOCKERS: Blockers = { rows: 0, cols: 0 };
export const ONE_BLOCKER: Blockers = { rows: 1, cols: 0 };
export const SIDES: readonly Side[] = ['both', 'offsuit', 'suited'];

/** Combos a hand keeps on a side once the given rows and columns are blocked (cells in a blocked line count once). */
export function comboCount(side: Side, rows: readonly number[], cols: readonly number[]): number {
  let n = 0;
  for (let r = 0; r < 4; r++) {
    if (rows.includes(r)) continue;
    for (let c = 0; c < 4; c++) {
      if (cols.includes(c)) continue;
      const suited = r === c;
      if (side === 'both' || (side === 'suited') === suited) n++;
    }
  }
  return n;
}

function subsets(size: number): number[][] {
  const out: number[][] = [];
  for (let mask = 0; mask < 16; mask++) {
    const s = [0, 1, 2, 3].filter((i) => mask & (1 << i));
    if (s.length === size) out.push(s);
  }
  return out;
}

/** Every A value a side can take with that many blocked lines (0 excluded), largest first. */
export function aValuesFor(blockers: Blockers): readonly { a: number; side: Side }[] {
  const seen = new Set<string>();
  const out: { a: number; side: Side }[] = [];
  for (const side of SIDES)
    for (const nr of countsOf(blockers.rows))
      for (const nc of countsOf(blockers.cols))
        for (const rows of subsets(nr))
          for (const cols of subsets(nc)) {
            const a = comboCount(side, rows, cols);
            const key = `${a}:${side}`;
            if (a >= 1 && !seen.has(key)) {
              seen.add(key);
              out.push({ a, side });
            }
          }
  return out.sort((x, y) => y.a - x.a);
}

/** Distinct A values across all sides, largest first (what the hint table and the distractors use). */
export const aNumbersFor = (blockers: Blockers): number[] => [
  ...new Set(aValuesFor(blockers).map((v) => v.a)),
];
